<template>
  <div class="recycle-kegiatan-container p-4">
    <Breadcrumbs :items="breadcrumbItems" />

    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h3 class="font-weight-bold text-dark mb-1">
          <VueFeather type="refresh-cw" size="24" class="mr-2 text-primary" style="vertical-align: middle;"></VueFeather>
          Recycle Metadata Kegiatan
        </h3>
        <p class="text-muted mb-0">
          Daur ulang dan salin siklus kegiatan statistik ke produsen data tujuan beserta seluruh metadata variabel dan indikator terkait.
        </p>
      </div>
      <div>
        <b-badge variant="light" class="p-2 border">
          <span class="text-muted mr-1">Role Anda:</span>
          <strong class="text-primary">{{ currentRoleName }}</strong>
        </b-badge>
      </div>
    </div>

    <!-- Alert jika role tidak berwenang -->
    <b-alert v-if="!canAccess" show variant="danger" class="mb-4">
      <h5><VueFeather type="alert-triangle" size="18" class="mr-2"></VueFeather> Akses Terbatas</h5>
      Halaman ini khusus diperuntukkan bagi <strong>Pembina Data Pusat (Direktorat Diseminasi Statistik)</strong> dan <strong>Superadmin</strong>.
    </b-alert>

    <div v-else>
      <b-row>
        <!-- Kolom Kiri: Input & Konfigurasi -->
        <b-col lg="7">
          <!-- Step 1: Tentukan ID Kegiatan Sumber -->
          <b-card no-body class="shadow-sm mb-4 border-0">
            <b-card-header class="bg-white border-bottom py-3">
              <div class="d-flex align-items-center">
                <span class="step-badge mr-2">1</span>
                <h5 class="mb-0 font-weight-bold">Tentukan ID Kegiatan Sumber</h5>
              </div>
            </b-card-header>
            <b-card-body>
              <b-form-group label="ID Kegiatan yang Akan Didaur Ulang (Recycle)" label-class="font-weight-bold">
                <b-input-group>
                  <b-form-input
                    v-model="sourceKegId"
                    type="number"
                    placeholder="Contoh: 1543 atau 2088"
                    :disabled="isLoadingKeg"
                    @keyup.enter="fetchSourceKegiatan"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button variant="primary" :disabled="!sourceKegId || isLoadingKeg" @click="fetchSourceKegiatan">
                      <b-spinner small v-if="isLoadingKeg"></b-spinner>
                      <VueFeather v-else type="search" size="14" class="mr-1"></VueFeather>
                      Cari Kegiatan
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
                <small class="text-muted">Masukkan ID Kegiatan yang ingin diduplikasi atau dibuat siklus barunya.</small>
              </b-form-group>

              <!-- Loading spinner -->
              <div v-if="isLoadingKeg" class="text-center py-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="text-muted mt-2">Memuat detail metadata kegiatan...</div>
              </div>

              <!-- Error state -->
              <b-alert v-if="kegError" show variant="danger" class="mt-3">
                <VueFeather type="alert-circle" size="16" class="mr-2"></VueFeather>
                {{ kegError }}
              </b-alert>

              <!-- Detail Kegiatan Ditemukan -->
              <div v-if="sourceKeg && !isLoadingKeg" class="source-keg-card p-3 rounded mt-3 border">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h6 class="font-weight-bold text-primary mb-1">
                    {{ sourceKeg.data ? (sourceKeg.data.judul_kegiatan || sourceKeg.data.judulKegiatan || '-') : '-' }}
                  </h6>
                  <b-badge :variant="getStatusVariant(sourceKeg.status)">
                    {{ sourceKeg.status || 'DRAFT' }}
                  </b-badge>
                </div>

                <div class="small text-muted mb-2">
                  <div><strong>ID Kegiatan:</strong> #{{ sourceKeg.id }}</div>
                  <div><strong>Tahun / Periode:</strong> {{ sourceKeg.data ? (sourceKeg.data.tahun || sourceKeg.submission_period) : sourceKeg.submission_period }}</div>
                  <div>
                    <strong>Produsen Asal:</strong>
                    {{ sourceKeg.produsen_data ? sourceKeg.produsen_data.name : '-' }}
                    <span v-if="sourceKeg.produsen_data && sourceKeg.produsen_data.province_code">
                      ({{ sourceKeg.produsen_data.province_code }}/{{ sourceKeg.produsen_data.city_code || 'Pusat' }})
                    </span>
                  </div>
                </div>

                <!-- Stat Pills -->
                <div class="d-flex gap-2 mt-2">
                  <b-badge variant="info" class="mr-2 px-2 py-1">
                    <VueFeather type="layers" size="12" class="mr-1"></VueFeather>
                    {{ (sourceKeg.ms_var || []).length }} Variabel
                  </b-badge>
                  <b-badge variant="success" class="px-2 py-1">
                    <VueFeather type="activity" size="12" class="mr-1"></VueFeather>
                    {{ (sourceKeg.ms_ind || []).length }} Indikator
                  </b-badge>
                </div>

                <!-- Accordion Detail Preview Variabel & Indikator -->
                <div class="mt-3">
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    @click="showDetailItems = !showDetailItems"
                    class="d-flex align-items-center"
                  >
                    <VueFeather :type="showDetailItems ? 'chevron-up' : 'chevron-down'" size="14" class="mr-1"></VueFeather>
                    {{ showDetailItems ? 'Sembunyikan Daftar Variabel & Indikator' : 'Lihat Daftar Variabel & Indikator' }}
                  </b-button>

                  <div v-if="showDetailItems" class="mt-2 pt-2 border-top">
                    <div class="mb-2">
                      <strong class="text-secondary small">Metadata Variabel ({{ (sourceKeg.ms_var || []).length }}):</strong>
                      <ul class="pl-3 mb-2 small text-muted" style="max-height: 120px; overflow-y: auto;">
                        <li v-for="(v, idx) in sourceKeg.ms_var || []" :key="'v-'+idx">
                          {{ v.data ? (v.data.nama || v.data.alias || '-') : '-' }}
                        </li>
                        <li v-if="!(sourceKeg.ms_var && sourceKeg.ms_var.length)" class="text-muted font-italic">Tidak ada variabel</li>
                      </ul>
                    </div>
                    <div>
                      <strong class="text-secondary small">Metadata Indikator ({{ (sourceKeg.ms_ind || []).length }}):</strong>
                      <ul class="pl-3 mb-0 small text-muted" style="max-height: 120px; overflow-y: auto;">
                        <li v-for="(ind, idx) in sourceKeg.ms_ind || []" :key="'ind-'+idx">
                          {{ ind.data ? ind.data.nama : '-' }}
                        </li>
                        <li v-if="!(sourceKeg.ms_ind && sourceKeg.ms_ind.length)" class="text-muted font-italic">Tidak ada indikator</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>

          <!-- Step 2: Tentukan Tujuan Produsen Data & Siklus -->
          <b-card no-body class="shadow-sm mb-4 border-0">
            <b-card-header class="bg-white border-bottom py-3">
              <div class="d-flex align-items-center">
                <span class="step-badge mr-2">2</span>
                <h5 class="mb-0 font-weight-bold">Tentukan Tujuan Produsen Data & Siklus</h5>
              </div>
            </b-card-header>
            <b-card-body>
              <b-form-group label="Instansi Produsen Data Tujuan" label-class="font-weight-bold">
                          <Treeselect
            clearable
            valueFormat="object"
            placeholder="Ketik untuk mencari instansi produsen data SDI baru..."
            v-model="targetProdusen"
            :async="true"
            :options="produsenOptions"
            :loadOptions="produsenDataLoadOptions"
            :normalizer="produsenDataNormalizer"
            :defaultOptions="true"
            :disable-branch-nodes="true"
            @select="onSelectTargetOrg"
            @deselect="onDeselectTargetOrg"
          />
                <small class="text-muted">Pilih instansi K/L/D/I yang akan menjadi pemilik kegiatan pada siklus baru.</small>
              </b-form-group>

              <!-- Target Org Info Badge -->
              <div v-if="selectedTargetOrg" class="target-org-info p-3 rounded mb-3 bg-light border">
                <div class="font-weight-bold text-dark">{{ selectedTargetOrg.name }}</div>
                <div class="small text-muted">
                  <span><strong>ID Instansi:</strong> {{ selectedTargetOrg.id }}</span>
                  <span class="mx-2">•</span>
                  <span><strong>Kode Wilayah:</strong> {{ selectedTargetOrg.provinceCode || '00' }} / {{ selectedTargetOrg.cityCode || '00' }}</span>
                </div>
              </div>

              <!-- Parameter Siklus Baru -->
              <b-row>
                <b-col md="6">
                  <b-form-group label="Tahun Pengajuan Siklus Baru" label-class="font-weight-bold">
                    <b-form-input
                      v-model="targetYear"
                      type="number"
                      placeholder="Contoh: 2026"
                    ></b-form-input>
                    <small class="text-muted">Tahun periode metadata baru.</small>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Status Hasil Daur Ulang" label-class="font-weight-bold">
                    <b-form-input value="DRAFT" disabled class="bg-light font-weight-bold text-secondary"></b-form-input>
                    <small class="text-muted">Siklus baru selalu dibuat sebagai DRAFT.</small>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Checkbox Options -->
              <div class="recycle-options mt-2 p-3 bg-light rounded border">
                <strong class="d-block mb-2 text-dark font-weight-bold">Opsi Metadata yang Didaur Ulang:</strong>
                <b-form-checkbox v-model="includeVariabel" class="mb-2">
                  Salin seluruh Metadata Variabel (<strong>{{ sourceKeg ? (sourceKeg.ms_var || []).length : 0 }}</strong> variabel)
                </b-form-checkbox>
                <b-form-checkbox v-model="includeIndikator" class="mb-2">
                  Salin seluruh Metadata Indikator (<strong>{{ sourceKeg ? (sourceKeg.ms_ind || []).length : 0 }}</strong> indikator)
                </b-form-checkbox>
                <b-form-checkbox v-model="updateBlokIInstansi">
                  Sesuaikan nama Instansi Penyelenggara di Blok I dengan Produsen Data Tujuan
                </b-form-checkbox>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Kolom Kanan: Preview & Eksekusi -->
        <b-col lg="5">
          <b-card no-body class="shadow-sm mb-4 border-0">
            <b-card-header class="bg-white border-bottom py-3">
              <div class="d-flex align-items-center">
                <span class="step-badge mr-2">3</span>
                <h5 class="mb-0 font-weight-bold">Review & Eksekusi Daur Ulang</h5>
              </div>
            </b-card-header>
            <b-card-body>
              <div class="review-box p-3 rounded mb-3 border bg-light">
                <h6 class="font-weight-bold text-dark mb-2">Ringkasan Operasi:</h6>
                
                <table class="table table-sm table-borderless small mb-0">
                  <tbody>
                    <tr>
                      <td class="text-muted" style="width: 130px;">Kegiatan Asal:</td>
                      <td class="font-weight-bold">
                        <span v-if="sourceKeg">#{{ sourceKeg.id }} - {{ sourceKeg.data ? (sourceKeg.data.judul_kegiatan || sourceKeg.data.judulKegiatan || '-') : '-' }}</span>
                        <span v-else class="text-muted font-italic">Belum dipilih</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Produsen Asal:</td>
                      <td>
                        <span v-if="sourceKeg && sourceKeg.produsen_data">{{ sourceKeg.produsen_data.name }}</span>
                        <span v-else class="text-muted font-italic">-</span>
                      </td>
                    </tr>
                    <tr class="border-top">
                      <td class="text-muted pt-2">Produsen Tujuan:</td>
                      <td class="pt-2 font-weight-bold text-primary">
                        <span v-if="selectedTargetOrg">{{ selectedTargetOrg.name }}</span>
                        <span v-else class="text-muted font-italic">Belum dipilih</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Tahun Siklus Baru:</td>
                      <td class="font-weight-bold">{{ targetYear }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Variabel Didaur:</td>
                      <td>{{ includeVariabel ? ((sourceKeg && sourceKeg.ms_var) ? sourceKeg.ms_var.length : 0) + ' variabel' : 'Dilewati' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Indikator Didaur:</td>
                      <td>{{ includeIndikator ? ((sourceKeg && sourceKeg.ms_ind) ? sourceKeg.ms_ind.length : 0) + ' indikator' : 'Dilewati' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Status Dibuat:</td>
                      <td><b-badge variant="secondary">DRAFT</b-badge></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Tombol Aksi -->
              <b-button
                variant="primary"
                block
                size="lg"
                class="font-weight-bold py-2 shadow-sm"
                :disabled="!canExecuteRecycle"
                @click="openConfirmModal"
              >
                <VueFeather type="refresh-cw" size="18" class="mr-2" style="vertical-align: middle;"></VueFeather>
                Mulai Recycle Kegiatan
              </b-button>
              <div v-if="!canExecuteRecycle" class="small text-muted text-center mt-2">
                Pilih kegiatan sumber dan tentukan instansi produsen data tujuan untuk mengaktifkan tombol eksekusi.
              </div>

              <!-- Hasil Kegiatan Baru jika selesai -->
              <div v-if="createdNewKegId" class="mt-4 p-3 border rounded bg-success-light">
                <div class="d-flex align-items-center text-success font-weight-bold mb-2">
                  <VueFeather type="check-circle" size="20" class="mr-2"></VueFeather>
                  Recycle Berhasil Dibuat!
                </div>
                <p class="small mb-3">
                  Kegiatan baru berhasil dibuat dengan ID: <strong>#{{ createdNewKegId }}</strong> dalam status <strong>DRAFT</strong>.
                </p>
                <div class="d-flex gap-2">
                  <b-button
                    :to="'/metadata/view-kegiatan/' + createdNewKegId"
                    target="_blank"
                    variant="success"
                    size="sm"
                    class="mr-2"
                  >
                    <VueFeather type="external-link" size="14" class="mr-1"></VueFeather>
                    Buka Detail Kegiatan Baru
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="resetForm"
                  >
                    Recycle Kegiatan Lain
                  </b-button>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Modal Konfirmasi & Live Execution -->
    <b-modal
      id="modal-recycle-process"
      ref="recycleModal"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      header-class="px-4 px-md-5 pt-4 pb-3 border-bottom-0 bg-white"
      body-class="px-4 px-md-5 py-2 bg-white"
      footer-class="px-4 px-md-5 pb-4 pt-3 border-top-0 bg-white"
    >
      <!-- Custom Header -->
      <template #modal-header>
        <div class="d-flex align-items-center w-100">
          <div 
            class="modal-icon-badge mr-3 d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            :class="{
              'bg-primary-light text-primary': executionState === 'confirm',
              'bg-info-light text-info': executionState === 'processing',
              'bg-success-light text-success': executionState === 'done' && progress.failed === 0,
              'bg-warning-light text-warning': executionState === 'done' && progress.failed > 0
            }"
            style="width: 44px; height: 44px;"
          >
            <b-spinner small v-if="executionState === 'processing'" variant="primary"></b-spinner>
            <VueFeather v-else-if="executionState === 'done'" :type="progress.failed > 0 ? 'alert-triangle' : 'check-circle'" size="22"></VueFeather>
            <VueFeather v-else type="refresh-cw" size="20"></VueFeather>
          </div>
          <div class="flex-grow-1">
            <h5 class="modal-title font-weight-bold mb-1 text-dark" style="font-size: 1.15rem; letter-spacing: -0.2px;">
              {{ executionState === 'confirm' ? 'Konfirmasi Recycle Metadata Kegiatan' : (executionState === 'done' ? 'Hasil Daur Ulang Metadata Kegiatan' : 'Memproses Daur Ulang Metadata...') }}
            </h5>
            <p class="text-muted small mb-0">
              {{ executionState === 'confirm' ? 'Periksa kembali parameter siklus dan instansi produsen data tujuan sebelum dieksekusi.' : (executionState === 'done' ? 'Proses replikasi siklus metadata ke backend MMS telah selesai.' : 'Menyimpan usulan kegiatan baru dan mereplikasi elemen terkait ke backend.') }}
            </p>
          </div>
        </div>
      </template>

      <!-- STATE 1: CONFIRM -->
      <div v-if="executionState === 'confirm'" class="py-1">
        <!-- Alert Info Penjelasan Siklus -->
        <div class="alert d-flex align-items-start mb-4 py-2 px-3 border-0 shadow-none rounded" style="background-color: #f0f7ff; color: #1e429f;">
          <VueFeather type="info" size="18" class="mr-2 mt-1 flex-shrink-0 text-primary"></VueFeather>
          <div class="small leading-normal">
            Kegiatan baru akan didaftarkan sebagai <strong>Siklus Lanjutan (Tahun {{ targetYear }})</strong> dengan status awal <strong>DRAFT</strong>. Seluruh struktur blok I-VIII akan diadaptasi untuk instansi tujuan.
          </div>
        </div>

        <!-- Transfer Overview Card -->
        <div class="summary-transfer-card p-3 p-md-4 rounded border mb-3" style="background-color: #fafbfc;">
          <!-- Baris Kegiatan Sumber -->
          <div class="mb-3 pb-3 border-bottom">
            <span class="text-11 text-uppercase font-weight-bold text-muted d-block mb-1" style="letter-spacing: 0.5px;">Kegiatan Sumber</span>
            <div class="d-flex align-items-baseline">
              <span class="badge badge-secondary px-2 py-1 mr-2 text-11 font-weight-bold">ID #{{ sourceKeg ? sourceKeg.id : '-' }}</span>
              <span class="font-weight-bold text-dark text-14">
                {{ sourceKeg && sourceKeg.data ? (sourceKeg.data.judul_kegiatan || sourceKeg.data.judulKegiatan) : '-' }}
              </span>
            </div>
          </div>

          <!-- Transfer Instansi Produsen Data -->
          <div class="row align-items-center mb-3 pb-3 border-bottom">
            <div class="col-md-5">
              <span class="text-11 text-uppercase font-weight-bold text-muted d-block mb-1" style="letter-spacing: 0.5px;">Produsen Asal</span>
              <div class="font-weight-bold text-dark text-13">
                {{ (sourceKeg && sourceKeg.produsen_data && sourceKeg.produsen_data.name) || (sourceKeg && sourceKeg.produsen_data_name) || '-' }}
              </div>
              <small class="text-muted" v-if="sourceKeg && sourceKeg.submission_period">Periode Asal: {{ sourceKeg.submission_period }}</small>
            </div>
            <div class="col-md-2 text-center py-2 py-md-0">
              <div class="transfer-arrow-pill d-inline-flex align-items-center justify-content-center rounded-circle bg-white border shadow-xs" style="width: 32px; height: 32px;">
                <VueFeather type="arrow-right" size="16" class="text-primary"></VueFeather>
              </div>
            </div>
            <div class="col-md-5">
              <span class="text-11 text-uppercase font-weight-bold text-primary d-block mb-1" style="letter-spacing: 0.5px;">Produsen Data Tujuan</span>
              <div class="font-weight-bold text-primary text-13">
                {{ selectedTargetOrg ? selectedTargetOrg.name : '-' }}
              </div>
              <small class="text-success font-weight-bold">Periode Baru: {{ targetYear }} (DRAFT)</small>
            </div>
          </div>

          <!-- Replikasi Elemen -->
          <div>
            <span class="text-11 text-uppercase font-weight-bold text-muted d-block mb-2" style="letter-spacing: 0.5px;">Elemen yang Direplikasi</span>
            <div class="d-flex flex-wrap align-items-center">
              <span class="badge badge-light border text-dark px-2 py-1 mr-2 mb-1 text-12">
                <VueFeather type="file-text" size="12" class="mr-1 text-primary"></VueFeather>
                1 Metadata Kegiatan
              </span>
              <span class="badge badge-light border text-dark px-2 py-1 mr-2 mb-1 text-12" v-if="includeVariabel">
                <VueFeather type="list" size="12" class="mr-1 text-info"></VueFeather>
                {{ (sourceKeg && sourceKeg.ms_var) ? sourceKeg.ms_var.length : 0 }} MS-Variabel
              </span>
              <span class="badge badge-light border text-dark px-2 py-1 mr-2 mb-1 text-12" v-if="includeIndikator">
                <VueFeather type="activity" size="12" class="mr-1 text-success"></VueFeather>
                {{ (sourceKeg && sourceKeg.ms_ind) ? sourceKeg.ms_ind.length : 0 }} MS-Indikator
              </span>
            </div>
          </div>
        </div>

        <p class="small text-muted mb-0 px-1">
          <VueFeather type="check-square" size="13" class="mr-1 text-muted"></VueFeather>
          Kegiatan hasil daur ulang akan memiliki referensi <code>base_id: #{{ sourceKeg ? sourceKeg.id : '' }}</code> sebagai jejak audit siklus.
        </p>
      </div>

      <!-- STATE 2 & 3: PROCESSING & DONE -->
      <div v-else class="py-1">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="font-weight-bold text-13">
            <span v-if="executionState === 'processing'" class="text-primary d-inline-flex align-items-center">
              <b-spinner small variant="primary" class="mr-2"></b-spinner> Sedang mereplikasi metadata ke MMS API...
            </span>
            <span v-else-if="progress.failed > 0" class="text-warning d-inline-flex align-items-center">
              <VueFeather type="alert-triangle" size="16" class="mr-2 text-warning"></VueFeather> Selesai dengan Sebagian Peringatan
            </span>
            <span v-else class="text-success d-inline-flex align-items-center">
              <VueFeather type="check-circle" size="16" class="mr-2 text-success"></VueFeather> Daur Ulang Metadata Selesai!
            </span>
          </span>
          <b-badge :variant="progress.failed > 0 ? 'warning' : (executionState === 'done' ? 'success' : 'primary')" class="px-2 py-1 text-12">
            {{ progress.done }} / {{ progress.total }} Item Diproses
          </b-badge>
        </div>

        <b-progress
          :value="progress.done"
          :max="progress.total"
          :variant="progress.failed > 0 ? 'warning' : 'primary'"
          striped
          :animated="executionState === 'processing'"
          height="8px"
          class="mb-3 rounded-pill shadow-xs"
        ></b-progress>

        <!-- Dark Terminal Console Log -->
        <div 
          id="recycle-console-log" 
          class="console-log-box p-3 rounded" 
          style="background-color: #1a1e24; color: #d4d4d4; font-family: 'Consolas', 'Monaco', monospace; font-size: 12px; height: 260px; overflow-y: auto; border: 1px solid #2d3748;"
        >
          <div v-for="(log, idx) in logs" :key="idx" v-html="log" class="mb-1"></div>
        </div>
      </div>

      <!-- Custom Footer Buttons -->
      <template #modal-footer>
        <div v-if="executionState === 'confirm'" class="d-flex justify-content-end align-items-center w-100">
          <b-button 
            variant="outline-secondary" 
            class="px-4 py-2 mr-2 font-weight-bold" 
            @click="$refs.recycleModal.hide()"
          >
            Batal
          </b-button>
          <b-button 
            variant="primary" 
            class="px-4 py-2 font-weight-bold shadow-sm d-inline-flex align-items-center" 
            @click="executeRecycle"
          >
            <VueFeather type="send" size="15" class="mr-2"></VueFeather> 
            Konfirmasi & Eksekusi Daur Ulang
          </b-button>
        </div>
        <div v-else class="d-flex justify-content-end align-items-center w-100">
          <b-button
            variant="primary"
            class="px-4 py-2 font-weight-bold shadow-sm"
            :disabled="executionState === 'processing'"
            @click="finishProcess"
          >
            Tutup
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import VueFeather from "vue-feather";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import MsService from "@/services/MsService.js";
import SdsService from "@/services/SdsService.js";
import SdiOrganizationService from "@/services/SdiOrganizationService.js";
import * as role from "@/views/ms/SdiRole.js";
import moment from "moment";

export default {
  name: "RecycleKegiatan",
  components: {
    Breadcrumbs,
    Treeselect,
    VueFeather,
  },
  data() {
    return {
      breadcrumbItems: [
        { text: "Beranda", href: "/" },
        { text: "Metadata", href: "/metadata-dashboard" },
        { text: "Pengaturan", href: "#" },
        { text: "Recycle Kegiatan", href: "/metadata/pengaturan/recycle-kegiatan", active: true },
      ],
      sourceKegId: "",
      sourceKeg: null,
      isLoadingKeg: false,
      kegError: null,
      showDetailItems: false,

      // Target selection
      targetProdusen: null,
      produsenOptions: null,
      targetProdusenDataId: null,
      selectedTargetOrg: null,
      targetYear: new Date().getFullYear(),
      includeVariabel: true,
      includeIndikator: true,
      updateBlokIInstansi: true,

      // Execution state
      executionState: "confirm", // 'confirm', 'processing', 'done'
      progress: { done: 0, total: 1, failed: 0 },
      logs: [],
      createdNewKegId: null,
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters["auth/user"] || {};
    },
    userRoles() {
      const u = this.currentUser;
      if (!u || !u.role) return [];
      if (Array.isArray(u.role)) return u.role;
      if (typeof u.role === "string") {
        const raw = u.role.trim();
        if (raw.startsWith("[") && raw.endsWith("]")) {
          return raw.slice(1, -1).split(",").map((s) => s.trim());
        }
        return raw.split(",").map((s) => s.trim());
      }
      return [];
    },
    currentRoleName() {
      const roles = this.userRoles;
      if (roles.includes("ROLE_SUPERADMIN")) return "Superadmin";
      if (roles.includes("ROLE_PEMBINA_DATA_PUSAT")) return "Pembina Data Pusat (Dit. Diseminasi)";
      return roles[0] || "User";
    },
    canAccess() {
      const roles = this.userRoles || [];
      const isSuper =
        roles.includes("ROLE_SUPERADMIN") ||
        roles.includes("ROLE_ADMIN") ||
        role.isSuperAdmin(roles);
      const isPembinaPusat =
        roles.includes("ROLE_PEMBINA_DATA_PUSAT") ||
        role.isPembinaDataPusat(roles);
      const isSpecialUser =
        this.currentUser &&
        (this.currentUser.email === "brian.firdian@gmail.com" ||
          this.currentUser.email === "brianf@bps.go.id");
      return isSuper || isPembinaPusat || isSpecialUser;
    },
    canExecuteRecycle() {
      return (
        this.canAccess &&
        this.sourceKeg &&
        this.sourceKeg.id &&
        this.selectedTargetOrg &&
        this.targetYear
      );
    },
  },
  methods: {
    getSubmitterPayload() {
      const u = this.currentUser || {};
      const sdiOrg = u.sdiOrganization || {};
      const sdiDetails = sdiOrg.details || {};
      const provCode = (sdiDetails.province && sdiDetails.province.code) || sdiDetails.province_code || u.idProvinsi || '00';
      const provName = (sdiDetails.province && sdiDetails.province.name) || u.provinsi || 'PUSAT';
      const cityCode = (sdiDetails.city && sdiDetails.city.code) || sdiDetails.city_code || u.idKabupatenKota || '0000';
      const cityName = (sdiDetails.city && sdiDetails.city.name) || u.kabupatenKota || 'PUSAT';

      return {
        id: u.id || 1,
        name: u.name || 'Administrator',
        username: u.username || 'admin',
        email: u.email || 'admin@bps.go.id',
        organization: {
          id: sdiOrg.id || u.idOrganisasi || (this.selectedTargetOrg && this.selectedTargetOrg.id) || 15375,
          name: sdiOrg.name || u.organisasi || (this.selectedTargetOrg && this.selectedTargetOrg.name) || 'Badan Pusat Statistik',
          address: sdiDetails.address || '-',
          phone_number: sdiDetails.phone_number || sdiDetails.phone || '-',
          email: sdiDetails.email || u.email || 'admin@bps.go.id',
          province_code: provCode,
          city_code: cityCode,
          province: {
            code: provCode,
            name: provName,
          },
          city: {
            code: cityCode,
            name: cityName,
          },
        },
      };
    },
    getStatusVariant(status) {
      if (!status) return "secondary";
      const s = status.toUpperCase();
      if (s === "APPROVED" || s === "SELESAI") return "success";
      if (s === "REJECTED" || s === "DITOLAK") return "danger";
      if (s === "EXAMINED" || s === "DIPERIKSA") return "warning";
      if (s === "SUBMITTED" || s === "DIAJUKAN") return "info";
      return "secondary";
    },
    async fetchSourceKegiatan() {
      if (!this.sourceKegId) return;
      this.isLoadingKeg = true;
      this.kegError = null;
      this.sourceKeg = null;

      try {
        const res = await MsService.getMsKegById(this.sourceKegId);
        const result = (res && res.data && res.data.result);
        if (result && result.length > 0) {
          this.sourceKeg = result[0];
          // Pre-set target year jika ada
          if (this.sourceKeg.data && this.sourceKeg.data.tahun) {
            const yr = parseInt(this.sourceKeg.data.tahun, 10);
            if (!isNaN(yr)) {
              this.targetYear = yr + 1;
            }
          }
          // Pre-fill target produsen data dari kegiatan asal jika belum dipilih
          if (this.sourceKeg.produsen_data_id && !this.selectedTargetOrg) {
            this.selectedTargetOrg = {
              id: this.sourceKeg.produsen_data_id,
              name: this.sourceKeg.produsen_data_name,
              provinceCode: this.sourceKeg.produsen_data_province_code,
              cityCode: this.sourceKeg.produsen_data_city_code,
            };
            this.targetProdusen = this.selectedTargetOrg;
            this.targetProdusenDataId = this.sourceKeg.produsen_data_id;
          }
        } else {
          this.kegError = `Kegiatan dengan ID ${this.sourceKegId} tidak ditemukan.`;
        }
      } catch (err) {
        console.error("fetchSourceKegiatan error:", err);
        this.kegError = `Gagal memuat kegiatan: ${(err && err.response && err.response.data && err.response.data.message) || (err && err.message) || "Server error"}`;
      } finally {
        this.isLoadingKeg = false;
      }
    },
        produsenDataNormalizer(node) {
      return {
        id: node.id,
        label: node.name,
      };
    },
    async produsenDataLoadOptions({ searchQuery, callback }) {
      let name = searchQuery || null;
      let page = 0;
      let roles = ['PRODUSEN_DATA', 'WALIDATA'];
      try {
        const result = await SdiOrganizationService.getAllSdiOrganization(page, name, null, null, roles);
        const options = (result && result.data && result.data.content) || [];
        callback(null, options);
      } catch (error) {
        console.error("Gagal load SdiOrganization:", error);
        callback(null, []);
      }
    },
    onSelectTargetOrg(node) {
      this.selectedTargetOrg = node;
      if (node && node.id) {
        this.targetProdusenDataId = node.id;
      }
    },
    onDeselectTargetOrg() {
      this.selectedTargetOrg = null;
      this.targetProdusenDataId = null;
    },
    openConfirmModal() {
      this.executionState = "confirm";
      this.logs = [];
      const totalVars = this.includeVariabel ? (this.sourceKeg.ms_var || []).length : 0;
      const totalInds = this.includeIndikator ? (this.sourceKeg.ms_ind || []).length : 0;
      this.progress = { done: 0, total: 1 + totalVars + totalInds, failed: 0 };
      this.$refs.recycleModal.show();
    },
    appendLog(text, level = "info") {
      const colors = {
        info: "#9cdcfe",
        success: "#4ec9b0",
        error: "#f48771",
        warn: "#dcdcaa",
        step: "#569cd6",
      };
      const ts = new Date().toLocaleTimeString();
      const safe = String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      this.logs.push(
        `<span style="color:#888">[${ts}]</span> <span style="color:${colors[level] || colors.info}">${safe}</span>`
      );
      this.$nextTick(() => {
        const el = document.getElementById("recycle-console-log");
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    // Format & Normalisasi data v1 / v2
    prepareKegiatanData(source) {
      let data = {};
      const rawData = typeof source.data === "string" ? JSON.parse(source.data) : (source.data || {});

      if (source.version === "v1") {
        let keg = rawData;
        data = {
          judul_kegiatan: keg.judulKegiatan,
          tahun: parseInt(this.targetYear, 10),
          jenis_statistik: source.statistic_type,
          cara_pengumpulan_data: keg.caraPengumpulanData,
          sektor_kegiatan: keg.sektorKegiatan,
          identitas_rekomendasi: null,
          blok_i: {
            instansi_penyelanggara: this.updateBlokIInstansi
              ? this.selectedTargetOrg.name
              : (keg.blokI && keg.blokI.instansiPenyelanggara),
            alamat_instansi_penyelenggara: {
              alamat: (keg.blokI && keg.blokI.alamatLengkapInstansiPenyelenggara) || "",
              telepon: (keg.blokI && keg.blokI.telepon) || "",
              email: (keg.blokI && keg.blokI.email) || "",
              faksimile: (keg.blokI && keg.blokI.faksimile) || "",
            },
          },
          blok_ii: {
            unit_penanggung_jawab: {
              eselon1: keg.blokII && keg.blokII.unitPenanggungJawabEselon1,
              eselon2: keg.blokII && keg.blokII.unitPenanggungJawabEselon2,
            },
            penanggung_jawab_teknis: {
              nama: keg.blokII && keg.blokII.namaPenanggungJawabTeknis,
              jabatan: keg.blokII && keg.blokII.jabatanPenanggungJawabTeknis,
              alamat: keg.blokII && keg.blokII.alamatPenanggungJawabTeknis,
              telepon: keg.blokII && keg.blokII.teleponPenanggungJawabTeknis,
              email: keg.blokII && keg.blokII.emailPenanggungJawabTeknis,
              faksimile: keg.blokII && keg.blokII.faksimilePenanggungJawabTeknis,
            },
          },
          blok_iii: {
            latar_belakang_kegiatan: keg.blokIII && keg.blokIII.latarBelakangKegiatan,
            tujuan_kegiatan: keg.blokIII && keg.blokIII.tujuanKegiatan,
            rencana_jadwal_kegiatan: {
              perencanaan_kegiatan: ((keg.blokIII && keg.blokIII.rencanaJadwalKegiatan && keg.blokIII.rencanaJadwalKegiatan.perencanaanKegiatan) || []).map((x) => ({
                awal: x.awal ? moment(x.awal).format("YYYY-MM-DD") : null,
                akhir: x.akhir ? moment(x.akhir).format("YYYY-MM-DD") : null,
              })),
              desain: ((keg.blokIII && keg.blokIII.rencanaJadwalKegiatan && keg.blokIII.rencanaJadwalKegiatan.desain) || []).map((x) => ({
                awal: x.awal ? moment(x.awal).format("YYYY-MM-DD") : null,
                akhir: x.akhir ? moment(x.akhir).format("YYYY-MM-DD") : null,
              })),
              pengumpulan_data: ((keg.blokIII && keg.blokIII.rencanaJadwalKegiatan && keg.blokIII.rencanaJadwalKegiatan.pengumpulanData) || []).map((x) => ({
                awal: x.awal ? moment(x.awal).format("YYYY-MM-DD") : null,
                akhir: x.akhir ? moment(x.akhir).format("YYYY-MM-DD") : null,
              })),
              pengolahan_data: ((keg.blokIII && keg.blokIII.rencanaJadwalKegiatan && keg.blokIII.rencanaJadwalKegiatan.pengolahanData) || []).map((x) => ({
                awal: x.awal ? moment(x.awal).format("YYYY-MM-DD") : null,
                akhir: x.akhir ? moment(x.akhir).format("YYYY-MM-DD") : null,
              })),
              analisis: ((keg.blokIII && keg.blokIII.rencanaJadwalKegiatan && keg.blokIII.rencanaJadwalKegiatan.analisis) || []).map((x) => ({
                awal: x.awal ? moment(x.awal).format("YYYY-MM-DD") : null,
                akhir: x.akhir ? moment(x.akhir).format("YYYY-MM-DD") : null,
              })),
              diseminasi_hasil: ((keg.blokIII && keg.blokIII.rencanaJadwalKegiatan && keg.blokIII.rencanaJadwalKegiatan.diseminasiHasil) || []).map((x) => ({
                awal: x.awal ? moment(x.awal).format("YYYY-MM-DD") : null,
                akhir: x.akhir ? moment(x.akhir).format("YYYY-MM-DD") : null,
              })),
              evaluasi: ((keg.blokIII && keg.blokIII.rencanaJadwalKegiatan && keg.blokIII.rencanaJadwalKegiatan.evaluasi) || []).map((x) => ({
                awal: x.awal ? moment(x.awal).format("YYYY-MM-DD") : null,
                akhir: x.akhir ? moment(x.akhir).format("YYYY-MM-DD") : null,
              })),
            },
            variabel_yang_dikumpulkan: ((keg.blokIII && keg.blokIII.variabelYangDikumpulkan) || []).map((x) => ({
              nama: x.nama,
              konsep: x.konsep,
              definisi: x.definisi,
              referensi_waktu: x.referensiWaktu,
            })),
          },
          blok_iv: {
            kegiatan_ini_dilakukan: keg.blokIV && keg.blokIV.kegiatanIniDilakukan,
            frekuensi_penyelanggara: keg.blokIV && keg.blokIV.frekuensiPenyelanggara,
            tipe_pengumpulan_data: keg.blokIV && keg.blokIV.tipePengumpulanData,
            cakupan_wilayah_pengumpulan_data: keg.blokIV && keg.blokIV.cakupanWilayahPengumpulanData,
            sebagian_cakupan_wilayah_pengumpulan_data:
              keg.blokIV &&
              keg.blokIV.cakupanWilayahPengumpulanData === "SEBAGIAN_WILAYAH_INDONESIA" &&
              keg.blokIV.sebagianCakupanWilayahPengumpulanData
                ? keg.blokIV.sebagianCakupanWilayahPengumpulanData
                : [],
            metode_pengumpulan_data: {
              wawancara: Boolean(keg.blokIV && keg.blokIV.metodePengumpulanData && keg.blokIV.metodePengumpulanData.wawancara),
              mengisi_kuesioner_sendiri: Boolean(keg.blokIV && keg.blokIV.metodePengumpulanData && keg.blokIV.metodePengumpulanData.mengisiKuesionerSendiri),
              pengamatan: Boolean(keg.blokIV && keg.blokIV.metodePengumpulanData && keg.blokIV.metodePengumpulanData.pengamatan),
              pengumpulan_data_sekunder: Boolean(keg.blokIV && keg.blokIV.metodePengumpulanData && keg.blokIV.metodePengumpulanData.pengumpulanDataSekunder),
              lainnya: Boolean(keg.blokIV && keg.blokIV.metodePengumpulanData && keg.blokIV.metodePengumpulanData.lainnya),
              metode_pengumpulan_lainnya: (keg.blokIV && keg.blokIV.metodePengumpulanData && keg.blokIV.metodePengumpulanData.metodePengumpulanLainnya) || null,
            },
            sarana_pengumpulan_data: {
              papi: Boolean(keg.blokIV && keg.blokIV.saranaPengumpulanData && keg.blokIV.saranaPengumpulanData.papi),
              capi: Boolean(keg.blokIV && keg.blokIV.saranaPengumpulanData && keg.blokIV.saranaPengumpulanData.capi),
              cati: Boolean(keg.blokIV && keg.blokIV.saranaPengumpulanData && keg.blokIV.saranaPengumpulanData.cati),
              cawi: Boolean(keg.blokIV && keg.blokIV.saranaPengumpulanData && keg.blokIV.saranaPengumpulanData.cawi),
              mail: Boolean(keg.blokIV && keg.blokIV.saranaPengumpulanData && keg.blokIV.saranaPengumpulanData.mail),
              lainnya: Boolean(keg.blokIV && keg.blokIV.saranaPengumpulanData && keg.blokIV.saranaPengumpulanData.lainnya),
              sarana_pengumpulan_lainnya: (keg.blokIV && keg.blokIV.saranaPengumpulanData && keg.blokIV.saranaPengumpulanData.saranaPengumpulanLainnya) || null,
            },
            unit_pengumpulan_data: {
              individu: Boolean(keg.blokIV && keg.blokIV.unitPengumpulanData && keg.blokIV.unitPengumpulanData.individu),
              rumah_tangga: Boolean(keg.blokIV && keg.blokIV.unitPengumpulanData && keg.blokIV.unitPengumpulanData.rumahTangga),
              usaha_atau_perusahaan: Boolean(keg.blokIV && keg.blokIV.unitPengumpulanData && keg.blokIV.unitPengumpulanData.usahaAtauPerusahaan),
              lainnya: Boolean(keg.blokIV && keg.blokIV.unitPengumpulanData && keg.blokIV.unitPengumpulanData.lainnya),
              unit_pengumpulan_data_lainnya: (keg.blokIV && keg.blokIV.unitPengumpulanData && keg.blokIV.unitPengumpulanData.unitPengumpulanDataLainnya) || null,
            },
          },
          blok_v: {
            jenis_rancangan_sampel: keg.blokV && keg.blokV.jenisRancanganSampel,
            metode_pemilihan_sampel_tahap_terakhir: keg.blokV && keg.blokV.metodePemilihanSampelTahapTerakhir,
            metode_yang_digunakan:
              keg.blokV && keg.blokV.metodeYangdigunakan === "MULTI_STAGE_SAMPLING"
                ? "PROBABILTY_PROPORTIONAL_TO_SIZE_SAMPLING"
                : (keg.blokV && keg.blokV.metodeYangdigunakan),
            kerangka_sampel_tahap_terakhir: keg.blokV && keg.blokV.kerangkaSampelTahapTerakhir,
            fraksi_sampel_keseluruhan: keg.blokV && keg.blokV.fraksiSampelKeseluruhan,
            nilai_perkiraan_sampling_error_variabel_utama: keg.blokV && keg.blokV.nilaiPerkiraanSamplingErrorVariabelUtama,
            unit_sampel: keg.blokV && keg.blokV.unitSampel,
            unit_observasi: keg.blokV && keg.blokV.unitObservasi,
          },
          blok_vi: {
            apakah_melakukan_uji_coba: Boolean(keg.blokVI && keg.blokVI.apakahMelakukanUjiCoba),
            metode_pemeriksaan_kualitas_pengumpulan_data: {
              kunjungan_kembali: Boolean(keg.blokVI && keg.blokVI.metodePemeriksaanKualitasPengumpulanData && keg.blokVI.metodePemeriksaanKualitasPengumpulanData.kunjunganKembali),
              supervisi: Boolean(keg.blokVI && keg.blokVI.metodePemeriksaanKualitasPengumpulanData && keg.blokVI.metodePemeriksaanKualitasPengumpulanData.supervisi),
              taskforce: Boolean(keg.blokVI && keg.blokVI.metodePemeriksaanKualitasPengumpulanData && keg.blokVI.metodePemeriksaanKualitasPengumpulanData.taskforce),
              lainnya: Boolean(keg.blokVI && keg.blokVI.metodePemeriksaanKualitasPengumpulanData && keg.blokVI.metodePemeriksaanKualitasPengumpulanData.lainnya),
              metode_pemeriksaan_kualitas_pengumpulan_data_lainnya:
                (keg.blokVI && keg.blokVI.metodePemeriksaanKualitasPengumpulanData && keg.blokVI.metodePemeriksaanKualitasPengumpulanData.metodePemeriksaanKualitasPengumpulanDataLainnya) || null,
            },
            apakah_melakukan_penyesuaian_nonrespon: Boolean(keg.blokVI && keg.blokVI.apakahMelakukanPenyesuaianNonrespon),
            petugas_pengumpulan_data: keg.blokVI && keg.blokVI.petugasPengumpulanData,
            persyaratan_pendidikan_terendah_petugas_pengumpulan_data:
              keg.blokVI && keg.blokVI.persyaratanPendidikanTerendahPetugasPengumpulanData,
            jumlah_petugas_supervisor: keg.blokVI && keg.blokVI.jumlahPetugasSupervisor,
            jumlah_petugas_enumerator: keg.blokVI && keg.blokVI.jumlahPetugasEnumerator,
            apakah_melakukan_pelatihan_petugas: Boolean(keg.blokVI && keg.blokVI.apakahMelakukanPelatihanPetugas),
          },
          blok_vii: {
            tahapan_pengolahan_data: {
              editing: Boolean(keg.blokVII && keg.blokVII.tahapanPengolahanData && (keg.blokVII.tahapanPengolahanData.editing || keg.blokVII.tahapanPengolahanData.udi)),
              coding: Boolean(keg.blokVII && keg.blokVII.tahapanPengolahanData && (keg.blokVII.tahapanPengolahanData.coding || keg.blokVII.tahapanPengolahanData.udi)),
              data_entry: Boolean(keg.blokVII && keg.blokVII.tahapanPengolahanData && (keg.blokVII.tahapanPengolahanData.data_entry || keg.blokVII.tahapanPengolahanData.udi)),
              validasi: Boolean(keg.blokVII && keg.blokVII.tahapanPengolahanData && (keg.blokVII.tahapanPengolahanData.validasi || keg.blokVII.tahapanPengolahanData.udi)),
            },
            metode_analisis: keg.blokVII && keg.blokVII.metodeAnalisis,
            unit_analsis: {
              individu: Boolean(keg.blokVII && keg.blokVII.unitAnalsis && keg.blokVII.unitAnalsis.individu),
              rumah_tangga: Boolean(keg.blokVII && keg.blokVII.unitAnalsis && keg.blokVII.unitAnalsis.rumahTangga),
              usaha_atau_perusahaan: Boolean(keg.blokVII && keg.blokVII.unitAnalsis && keg.blokVII.unitAnalsis.usahaAtauPerusahaan),
              lainnya: Boolean(keg.blokVII && keg.blokVII.unitAnalsis && keg.blokVII.unitAnalsis.lainnya),
              unit_analisis_lainnya: (keg.blokVII && keg.blokVII.unitAnalsis && keg.blokVII.unitAnalsis.unitAnalisisLainnya) || null,
            },
            tingkat_penyajian_hasil_analisis: {
              nasional: Boolean(keg.blokVII && keg.blokVII.tingkatPenyajianHasilAnalisis && keg.blokVII.tingkatPenyajianHasilAnalisis.nasional),
              provinsi: Boolean(keg.blokVII && keg.blokVII.tingkatPenyajianHasilAnalisis && keg.blokVII.tingkatPenyajianHasilAnalisis.provinsi),
              kabupaten_atau_kota: Boolean(keg.blokVII && keg.blokVII.tingkatPenyajianHasilAnalisis && keg.blokVII.tingkatPenyajianHasilAnalisis.kabupatenAtauKota),
              kecamatan: Boolean(keg.blokVII && keg.blokVII.tingkatPenyajianHasilAnalisis && keg.blokVII.tingkatPenyajianHasilAnalisis.kecamatan),
              lainnya: Boolean(keg.blokVII && keg.blokVII.tingkatPenyajianHasilAnalisis && keg.blokVII.tingkatPenyajianHasilAnalisis.lainnya),
              tingkat_penyajian_hasil_analisis_lainnya:
                (keg.blokVII && keg.blokVII.tingkatPenyajianHasilAnalisis && keg.blokVII.tingkatPenyajianHasilAnalisis.tingkatPenyajianHasilAnalisisLainnya) || null,
            },
          },
          blok_viii: {
            ketersediaan_produk_tercetak: Boolean(keg.blokVIII && keg.blokVIII.ketersediaanProdukTercetak),
            ketersediaan_produk_digital: Boolean(keg.blokVIII && keg.blokVIII.ketersediaanProdukDigital),
            ketersediaan_produk_mikrodata: Boolean(keg.blokVIII && keg.blokVIII.ketersediaanProdukMikrodata),
            rencana_jadwal_rilis_produk_tercetak: (keg.blokVIII && keg.blokVIII.ketersediaanProdukTercetak && keg.blokVIII.rencanaJadwalRilisProdukTercetak)
              ? (keg.blokVIII.rencanaJadwalRilisProdukTercetak || []).map((x) => x.tanggalRilis ? moment(x.tanggalRilis).format("YYYY-MM-DD") : null)
              : null,
            rencana_jadwal_rilis_produk_digital: (keg.blokVIII && keg.blokVIII.ketersediaanProdukDigital && keg.blokVIII.rencanaJadwalRilisProdukDigital)
              ? (keg.blokVIII.rencanaJadwalRilisProdukDigital || []).map((x) => x.tanggalRilis ? moment(x.tanggalRilis).format("YYYY-MM-DD") : null)
              : null,
            rencana_jadwal_rilis_produk_mikrodata: (keg.blokVIII && keg.blokVIII.ketersediaanProdukMikrodata && keg.blokVIII.rencanaJadwalRilisProdukMikrodata)
              ? (keg.blokVIII.rencanaJadwalRilisProdukMikrodata || []).map((x) => x.tanggalRilis ? moment(x.tanggalRilis).format("YYYY-MM-DD") : null)
              : null,
          },
        };
      } else {
        // v2 deep clone
        data = JSON.parse(JSON.stringify(rawData));
        data.tahun = parseInt(this.targetYear, 10);
        if (this.updateBlokIInstansi && data.blok_i) {
          data.blok_i.instansi_penyelanggara = this.selectedTargetOrg.name;
          if (this.selectedTargetOrg.details && data.blok_i.alamat_instansi_penyelenggara) {
            const det = this.selectedTargetOrg.details;
            if (det.address) data.blok_i.alamat_instansi_penyelenggara.alamat = det.address;
            if (det.phone) data.blok_i.alamat_instansi_penyelenggara.telepon = det.phone;
            if (det.email) data.blok_i.alamat_instansi_penyelenggara.email = det.email;
          }
        }
      }
      return data;
    },

    normalizeMsVarData(msvarItem) {
      let data = {};
      const rawData = typeof msvarItem.data === "string" ? JSON.parse(msvarItem.data) : (msvarItem.data || {});

      if (msvarItem.version === "v1") {
        let variable = rawData;
        data = {
          nama: variable.nama,
          alias: variable.alias,
          konsep: variable.konsep ? [variable.konsep] : [null],
          definisi: variable.definisi,
          referensi_pemilihan:
            variable.referensiPemilihan && variable.referensiPemilihan.length > 0
              ? variable.referensiPemilihan.map((x) => x.nama)
              : [],
          referensi_waktu: variable.referensiWaktu,
          ukuran: variable.ukuran,
          satuan: variable.satuan,
          tipe_data: variable.tipeData,
          value_domain:
            variable.valueDomain && variable.valueDomain.length > 0 ? variable.valueDomain : [],
          aturan_validasi:
            variable.aturanValidasi && variable.aturanValidasi.length > 0
              ? variable.aturanValidasi
              : [null],
          kalimat_pertanyaan: variable.kalimatPertanyaan,
          apakah_variabel_bisa_diakses_umum: variable.apakahVariabelBisaDiaksesUmum,
          id_sds: null,
        };
      } else {
        data = JSON.parse(JSON.stringify(rawData));
      }
      if (data.value_domain == null || data.value_domain === undefined) {
        data.value_domain = [];
      }
      return data;
    },

    normalizeMsIndData(msindItem) {
      let data = {};
      const rawData = typeof msindItem.data === "string" ? JSON.parse(msindItem.data) : (msindItem.data || {});

      if (msindItem.version === "v1") {
        let indikator = rawData;
        data = {
          nama: indikator.nama,
          konsep: indikator.konsep ? [indikator.konsep] : [null],
          definisi: indikator.definisi,
          interpretasi: indikator.interpretasi,
          metode_perhitungan: indikator.metodeAtauRumusPerhitungan,
          rumus: null,
          ukuran: indikator.ukuran,
          satuan: indikator.satuan,
          variabel_disaggregasi:
            indikator.variabelDisaggregasi && indikator.variabelDisaggregasi.length > 0
              ? indikator.variabelDisaggregasi
              : [{ nama: null, sumber: "input-manual" }],
          apakah_indikator_komposit: indikator.apakahIndikatorKomposit,
          indikator_pembangun:
            indikator.indikatorPembangun && indikator.indikatorPembangun.length > 0
              ? indikator.indikatorPembangun.map((x) => ({
                  nama: x.nama,
                  sumber_publikasi: (x.ketersediaanPublikasi || []).map((p) => p.nama),
                }))
              : [],
          variabel_pembangun:
            indikator.variabelPembangun && indikator.variabelPembangun.length > 0
              ? indikator.variabelPembangun.map((x) => ({
                  nama: x.nama,
                  kegiatan_penghasil: x.kegiatanPenghasil,
                }))
              : [],
          level_estimasi: indikator.levelEstimasi,
          apakah_indikator_bisa_diakses_umum: indikator.apakahIndikatorBisaDiaksesUmum,
          id_sds: null,
        };
        data.variabel_disaggregasi = (data.variabel_disaggregasi || []).map((x) => {
          if (typeof x === "string" || x == null) {
            return data.id_sds ? { nama: x, sumber: "sds" } : { nama: x, sumber: "input-manual" };
          }
          return x;
        });
      } else {
        data = JSON.parse(JSON.stringify(rawData));
      }
      return data;
    },

    async executeRecycle() {
      this.executionState = "processing";
      this.logs = [];
      this.createdNewKegId = null;

      const targetOrg = {
        id: this.selectedTargetOrg.id,
        name: this.selectedTargetOrg.name,
        province_code: this.selectedTargetOrg.provinceCode || (this.selectedTargetOrg.details && this.selectedTargetOrg.details.province && this.selectedTargetOrg.details.province.code) || "00",
        city_code: this.selectedTargetOrg.cityCode || (this.selectedTargetOrg.details && this.selectedTargetOrg.details.city && this.selectedTargetOrg.details.city.code) || "0000",
      };

      let wpId = (this.selectedTargetOrg && this.selectedTargetOrg.details && this.selectedTargetOrg.details.walidata && this.selectedTargetOrg.details.walidata.id) ||
                 (this.sourceKeg && this.sourceKeg.walidata_pusat_id) || null;
      let wpName = (this.selectedTargetOrg && this.selectedTargetOrg.details && this.selectedTargetOrg.details.walidata && this.selectedTargetOrg.details.walidata.name) ||
                   (this.sourceKeg && this.sourceKeg.walidata_pusat_name) || null;

      if (!wpId || wpId === "" || isNaN(parseInt(wpId, 10))) {
        wpId = null;
      } else {
        wpId = parseInt(wpId, 10);
      }

      const walidataPusat = wpId ? {
        id: wpId,
        name: wpName || null,
      } : null;

      const submitterObj = this.getSubmitterPayload();

      this.appendLog("=== Memulai Recycle Metadata Kegiatan ID #" + this.sourceKeg.id + " ===", "step");
      this.appendLog("Target Produsen: " + targetOrg.name + " (Tahun " + this.targetYear + ")", "info");

      let newKegId = null;

      try {
        // Step 1: Simpan Kegiatan Baru
        this.appendLog("[1/3] Menyiapkan payload siklus kegiatan baru...", "step");
        const kegData = this.prepareKegiatanData(this.sourceKeg);
        const kegPayload = {
          base_id: this.sourceKeg.id,
          submission_period: parseInt(this.targetYear, 10),
          data: kegData,
          reviews: null,
          status: "DRAFT",
          rejection_note: null,
          submitter: submitterObj,
          produsen_data: targetOrg,
          walidata_pusat: walidataPusat,
          version: "v2",
          changes: null,
        };

        const resKeg = await MsService.saveNewMsKeg(kegPayload);
        const resData = resKeg && resKeg.data;
        const resItem = (resData && resData.result && resData.result[0]) || (resData && resData.result) || resData;

        if (resItem && (resItem.status === 403 || resItem.status === 500 || resItem.status === 400 || resItem.error)) {
          const detail = resItem.detail || resItem.title || resItem.message || resItem.error || ("Status HTTP " + resItem.status);
          throw new Error("Server menolak penyimpanan: " + detail);
        }

        newKegId = (resItem && resItem.id) || (resData && resData.id);
        if (!newKegId) {
          const rawDetail = resItem ? JSON.stringify(resItem) : JSON.stringify(resData);
          throw new Error("Gagal memperoleh ID Kegiatan baru dari server. Respons: " + rawDetail);
        }

        this.createdNewKegId = newKegId;
        this.progress.done += 1;
        this.appendLog("✓ MS-Keg baru berhasil dibuat dengan ID: #" + newKegId + " (Status: DRAFT)", "success");

        // Step 2: Daur ulang Metadata Variabel
        const vars = this.sourceKeg.ms_var || [];
        if (this.includeVariabel && vars.length > 0) {
          this.appendLog("[2/3] Memproses " + vars.length + " Metadata Variabel...", "step");
          for (let i = 0; i < vars.length; i++) {
            const vItem = vars[i];
            const vData = this.normalizeMsVarData(vItem);
            const varPayload = {
              ms_keg_id: newKegId,
              submission_period: parseInt(this.targetYear, 10),
              data: vData,
              reviews: null,
              status: "DRAFT",
              rejection_note: null,
              submitter: submitterObj,
              produsen_data: targetOrg,
              walidata_pusat: walidataPusat,
              version: "v2",
              changes: null,
            };

            try {
              const resVar = await MsService.saveNewMsVar(varPayload);
              const rvItem = (resVar && resVar.data && resVar.data.result && resVar.data.result[0]) || (resVar && resVar.data);
              if (rvItem && (rvItem.status === 403 || rvItem.status === 500 || rvItem.error)) {
                throw new Error(rvItem.detail || rvItem.title || rvItem.message || "Gagal");
              }
              this.progress.done += 1;
              this.appendLog("  ✓ [" + (i + 1) + "/" + vars.length + "] MS-Var \"" + (vData.nama || "-") + "\" tersimpan", "success");
            } catch (err) {
              console.error("Gagal simpan var:", err);
              this.progress.failed += 1;
              const errText = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || "Error";
              this.appendLog("  ✗ [" + (i + 1) + "/" + vars.length + "] MS-Var \"" + (vData.nama || "-") + "\" gagal: " + errText, "error");
            }
          }
        } else {
          this.appendLog("[2/3] Metadata Variabel dilewati (tidak dipilih atau kosong)", "info");
        }

        // Step 3: Daur ulang Metadata Indikator
        const inds = this.sourceKeg.ms_ind || [];
        if (this.includeIndikator && inds.length > 0) {
          this.appendLog("[3/3] Memproses " + inds.length + " Metadata Indikator...", "step");
          for (let i = 0; i < inds.length; i++) {
            const indItem = inds[i];
            const indData = this.normalizeMsIndData(indItem);
            const indPayload = {
              ms_keg_id: newKegId,
              submission_period: parseInt(this.targetYear, 10),
              data: indData,
              reviews: null,
              status: "DRAFT",
              rejection_note: null,
              submitter: submitterObj,
              produsen_data: targetOrg,
              walidata_pusat: walidataPusat,
              version: "v2",
              changes: null,
            };

            try {
              const resInd = await MsService.saveNewMsInd(indPayload);
              const riItem = (resInd && resInd.data && resInd.data.result && resInd.data.result[0]) || (resInd && resInd.data);
              if (riItem && (riItem.status === 403 || riItem.status === 500 || riItem.error)) {
                throw new Error(riItem.detail || riItem.title || riItem.message || "Gagal");
              }
              this.progress.done += 1;
              this.appendLog("  ✓ [" + (i + 1) + "/" + inds.length + "] MS-Ind \"" + (indData.nama || "-") + "\" tersimpan", "success");
            } catch (err) {
              console.error("Gagal simpan ind:", err);
              this.progress.failed += 1;
              const errText = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || "Error";
              this.appendLog("  ✗ [" + (i + 1) + "/" + inds.length + "] MS-Ind \"" + (indData.nama || "-") + "\" gagal: " + errText, "error");
            }
          }
        } else {
          this.appendLog("[3/3] Metadata Indikator dilewati (tidak dipilih atau kosong)", "info");
        }

        this.appendLog(
          "=== SELESAI: Berhasil " + (this.progress.done - 1) + " item metadata disalin ke Kegiatan #" + newKegId + " ===",
          this.progress.failed > 0 ? "warn" : "success"
        );
        this.executionState = "done";
      } catch (err) {
        console.error("executeRecycle error:", err);
        this.progress.failed += 1;
        let errMsg = "Terjadi kesalahan";
        if (err && err.response && err.response.data) {
          const d = err.response.data;
          const r = (d.result && d.result[0]) || d;
          errMsg = r.detail || r.message || r.title || (typeof d === "string" ? d : JSON.stringify(d));
        } else if (err && err.message) {
          errMsg = err.message;
        }
        this.appendLog("✗ Proses recycle gagal: " + errMsg, "error");
        this.executionState = "done";
      }
    },
    finishProcess() {
      this.$refs.recycleModal.hide();
    },
    resetForm() {
      this.sourceKegId = "";
      this.sourceKeg = null;
      this.targetProdusenDataId = null;
      this.selectedTargetOrg = null;
      this.createdNewKegId = null;
      this.logs = [];
    },
  },
};
</script>

<style scoped>
.step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #007bff;
  color: #fff;
  font-weight: bold;
  font-size: 12px;
}
.source-keg-card {
  background-color: #f8f9fa;
}
.bg-success-light {
  background-color: #e8f5e9;
  border-color: #c8e6c9 !important;
}
.console-log-box {
  line-height: 1.5;
}

.bg-primary-light {
  background-color: #e8f4fd !important;
}
.bg-info-light {
  background-color: #e3f2fd !important;
}
.bg-warning-light {
  background-color: #fff8e1 !important;
}
.text-11 { font-size: 11px; }
.text-12 { font-size: 12px; }
.text-13 { font-size: 13px; }
.text-14 { font-size: 14px; }
.shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
.modal-icon-badge {
  flex-shrink: 0;
}
.summary-transfer-card {
  border-color: #e2e8f0 !important;
}
</style>
